<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true" />
  <p-toast position="bottom-right" key="br" />
  <p-confirmDialog />

  <header>
    <div class="headline row-start-center gap-3">
      <h1>{{ headerTitle() }}</h1>
      @if (!isNew()) {
        <p-tag [severity]="statusSeverity(status())" [value]="statusLabel(status()) | translateWF: statusLabel(status())" />
      }
    </div>
    <div class="buttons">
      <!-- #5223 — a draft has posted nothing, so it can be removed outright. Hidden until the record is
           saved (there is nothing to delete before that) and again once it leaves Draft. The API applies
           the same rule; this only keeps the button off screen when it would be refused. -->
      @if (isDraft() && currentId()) {
        <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (click)="confirmDelete()" />
      }

      <span class="separator"></span>
      @if (isNew()) {
        <p-button [label]="'CashRequest_Create' | translateWF: 'Create draft'" icon="pi pi-save" severity="success" (click)="createDraft()" />
      } @else {
        @if (isDraft()) {
          <!-- "Plan documents" moved down beside "Add line" (#5135): both put lines on the table, and the
               header is where the REQUEST is acted on — saved, submitted, cancelled. -->
          <p-button [label]="'CashRequest_SaveChanges' | translateWF: 'Save changes'" icon="pi pi-save" variant="outlined" (click)="saveDraft()" />
          <p-button [label]="'CashRequest_Submit' | translateWF: 'Submit'" icon="pi pi-send" severity="success" [disabled]="!hasLines()" (click)="submit()" />
        }
        @if (isSubmitted()) {
          <p-button [label]="'CashRequest_Approve' | translateWF: 'Approve'" icon="pi pi-check" severity="success" (click)="approve()" />
        }
        @if (isDraft() || isSubmitted()) {
          <p-button [label]="'CashRequest_CancelBtn' | translateWF: 'Cancel request'" icon="pi pi-ban" severity="danger" variant="outlined" (click)="cancel()" />
        }
      }
      @if (!isNew()) {
        <p-button [label]="'CashRequest_Export' | translateWF: 'Export'" icon="pi pi-file-excel" variant="outlined" [disabled]="isExporting()" (click)="exportExcel()" />
      }
      <p-button [label]="'Back' | translateWF: 'Back'" icon="pi pi-arrow-left" variant="outlined" (click)="back()" />
    </div>
  </header>

  <div class="content">
    <div class="grid-12 gap-4">
      <div class="span-12-12-9 col">
        <!-- ───────────────────────── NEW: header only ───────────────────────── -->
        @if (isNew()) {
          <form [formGroup]="form">
            <p-panel>
              <p-card>
                <div class="grid-12">
                  <!-- Mondays only: the week IS its Monday, and the Sunday is Monday + 6. -->
                  <div class="span-12-4">
                    <div class="form-box">
                      <label>{{ 'CashRequest_Week' | translateWF: 'Week' }} <span class="text-red-500">*</span></label>
                      <p-datepicker
                        formControlName="weekStart"
                        appendTo="body"
                        styleClass="w-full"
                        [showIcon]="true"
                        [readonlyInput]="true"
                        [firstDayOfWeek]="1"
                        [disabledDays]="nonMondays"
                      />
                      @if (weekEnd(); as end) {
                        <small class="text-surface-500"> {{ form.controls.weekStart.value | date: 'EEEE dd/MM/yyyy' }} – {{ end | date: 'EEEE dd/MM/yyyy' }} </small>
                      }
                    </div>
                  </div>
                  <div class="span-12-2">
                    <div class="form-box">
                      <label>{{ 'CashRequest_Currency' | translateWF: 'Currency' }}</label>
                      <span>{{ currencyLabel() }}</span>
                      <small class="text-surface-500">{{
                        'CashRequest_Currency_Hint'
                          | translateWF
                            : 'Cash requests are
                      planned in USD.'
                      }}</small>
                    </div>
                  </div>
                  <div class="span-12-6">
                    <div class="form-box">
                      <label>{{ 'CashRequest_Notes' | translateWF: 'Notes' }}</label>
                      <input pInputText formControlName="notes" class="w-full" />
                    </div>
                  </div>
                </div>

                <div class="info-note mt-4">
                  <i class="pi pi-info-circle"></i>
                  <span>{{
                    'CashRequest_CreateInfo'
                      | translateWF
                        : 'Create the draft first, then plan payment requests and withholdings into it. Each
                  account they belong to becomes a line.'
                  }}</span>
                </div>
              </p-card>
            </p-panel>
          </form>
        } @else {
          <!-- ───────────────────────── EXISTING: header + planned lines ───────────────────────── -->
          @if (request(); as req) {
            @if (isDraft()) {
              <!-- Draft: editable header (week + notes; currency is locked after creation) -->
              <p-panel>
                <p-card>
                  <div class="grid-12 items-end">
                    <div class="span-12-4">
                      <div class="form-box">
                        <label>{{ 'CashRequest_Week' | translateWF: 'Week' }}</label>
                        <p-datepicker
                          [formControl]="form.controls.weekStart"
                          appendTo="body"
                          styleClass="w-full"
                          [showIcon]="true"
                          [readonlyInput]="true"
                          [firstDayOfWeek]="1"
                          [disabledDays]="nonMondays"
                        />
                        @if (weekEnd(); as end) {
                          <small class="text-surface-500"> {{ form.controls.weekStart.value | date: 'EEEE dd/MM/yyyy' }} – {{ end | date: 'EEEE dd/MM/yyyy' }} </small>
                        }
                      </div>
                    </div>
                    <div class="span-12-2">
                      <div class="form-box">
                        <label>{{ 'CashRequest_Currency' | translateWF: 'Currency' }}</label>
                        <span>{{ currencyLabel() }}</span>
                      </div>
                    </div>
                    <div class="span-12-2">
                      <div class="form-box">
                        <label>{{ 'CashRequest_RequestedBy' | translateWF: 'Requested by' }}</label>
                        <span>{{ req.requestedBy || req.createdBy || '—' }}</span>
                      </div>
                    </div>
                    <div class="span-12-4">
                      <div class="form-box">
                        <label>{{ 'CashRequest_Notes' | translateWF: 'Notes' }}</label>
                        <input pInputText [formControl]="form.controls.notes" class="w-full" />
                      </div>
                    </div>
                  </div>
                </p-card>
              </p-panel>
            } @else {
              <p-panel>
                <p-card>
                  <div class="grid-12">
                    <div class="span-12-3">
                      <div class="form-box">
                        <label>{{ 'CashRequest_Week' | translateWF: 'Week' }}</label>
                        <span>{{ req.weekStart | date: 'dd/MM/yyyy' }} – {{ req.weekEnd | date: 'dd/MM/yyyy' }}</span>
                      </div>
                    </div>
                    <div class="span-12-2">
                      <div class="form-box">
                        <label>{{ 'CashRequest_Currency' | translateWF: 'Currency' }}</label>
                        <span>{{ currencyLabel() }}</span>
                      </div>
                    </div>
                    <div class="span-12-3">
                      <div class="form-box">
                        <label>{{ 'CashRequest_RequestedBy' | translateWF: 'Requested by' }}</label>
                        <span>{{ req.requestedBy || req.createdBy || '—' }}</span>
                      </div>
                    </div>
                    <div class="span-12-4">
                      <div class="form-box">
                        <label>{{ 'CashRequest_Notes' | translateWF: 'Notes' }}</label>
                        <span>{{ req.notes || '—' }}</span>
                      </div>
                    </div>
                  </div>
                </p-card>
              </p-panel>
            }

            <!-- One line per GL account; what was planned against it lives in the expanded breakdown. -->
            <p-panel [header]="'CashRequest_Lines' | translateWF: 'Planned lines'">
              <ng-template pTemplate="fotter">
                <small class="text-surface-500 font-normal">{{
                  'CashRequest_Lines_Hint'
                    | translateWF
                      : 'One line per account. Expand a line to see everything planned
              against it.'
                }}</small>
              </ng-template>
              <!-- Two ways in, side by side, because they are two different jobs: documents route themselves
                   to an account, while a planilla has none and needs the account named by hand (#5135). -->
              @if (isDraft()) {
                <ng-template pTemplate="icons">
                  <p-button [label]="'CashRequest_PlanDocuments' | translateWF: 'Plan documents'" icon="pi pi-file-import" size="small" variant="outlined" (click)="openPicker()" />
                  <p-button
                    [label]="'CashRequest_Line_Add' | translateWF: 'Add line'"
                    icon="pi pi-plus"
                    size="small"
                    [pTooltip]="'CashRequest_Line_Add_Hint' | translateWF: 'Plan an amount against an account by hand — payroll, taxes, anything with no document'"
                    (click)="openLineModal()"
                  />
                </ng-template>
              }

              <p-card data-variant="compact">
                <p-table [value]="req.lines" dataKey="lineId" [expandedRowKeys]="expandedRows" [rowHover]="true" styleClass="p-datatable-sm">
                  <ng-template pTemplate="header">
                    <tr>
                      <th style="width: 3rem"></th>
                      <th style="width: 20rem">{{ 'CashRequest_Account' | translateWF: 'Account' }}</th>
                      <th>{{ 'CashRequest_LineDesc' | translateWF: 'Description' }}</th>
                      <th class="text-right" style="width: 10rem">{{ 'CashRequest_Documents' | translateWF: 'Documents' }}</th>
                      <th class="text-right" style="width: 10rem">{{ 'CashRequest_Manual' | translateWF: 'Manual' }}</th>
                      <th class="text-right" style="width: 10rem">{{ 'CashRequest_Amount' | translateWF: 'Total (USD)' }}</th>
                      <th class="text-center" style="width: 9rem">{{ 'CashRequest_Attachment' | translateWF: 'Attachment' }}</th>
                      @if (isDraft()) {
                        <th style="width: 4rem"></th>
                      }
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="body" let-line let-expanded="expanded">
                    <tr>
                      <td>
                        <p-button type="button" pRowToggler [pRowToggler]="line" [text]="true" [rounded]="true" [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" />
                      </td>
                      <td>
                        <div class="col">
                          <span>{{ (line.accountName | localized) || line.glAccount?.number || '—' }}</span>
                          @if (line.accountName) {
                            <span class="font-mono text-xs text-[var(--p-text-muted-color)]">{{ line.glAccount?.number }}</span>
                          }
                        </div>
                      </td>
                      <td>
                        @if (isDraft()) {
                          <input pInputText [formControl]="descControl(line.lineId)" class="w-full" />
                        } @else {
                          {{ line.description || '—' }}
                        }
                      </td>
                      <td class="text-right tabular-nums">{{ line.documentTotal | number: '1.2-2' }}</td>
                      <td class="text-right tabular-nums">{{ line.manualTotal | number: '1.2-2' }}</td>
                      <td class="text-right tabular-nums font-semibold">{{ line.amount | number: '1.2-2' }}</td>
                      <td class="text-center">
                        <input #lineFile type="file" accept="application/pdf,image/*" class="hidden" (change)="onLineFile($event, line.lineId, lineFile)" />
                        @if (line.attachmentUrl) {
                          <p-button icon="pi pi-file-pdf" severity="info" [text]="true" [rounded]="true" (click)="openAttachment(line.attachmentUrl)" />
                        }
                        @if (isDraft()) {
                          <p-button icon="pi pi-paperclip" [text]="true" [rounded]="true" (click)="lineFile.click()" />
                        } @else if (!line.attachmentUrl) {
                          <span class="text-[var(--p-text-muted-color)]">—</span>
                        }
                      </td>
                      @if (isDraft()) {
                        <td class="text-center whitespace-nowrap">
                          <!-- Adds to an account the picker already opened. Only the manual entries are
                             touched — the documents on this line are the picker's. -->
                          <p-button
                            icon="pi pi-pencil"
                            [text]="true"
                            [rounded]="true"
                            [pTooltip]="'CashRequest_Line_Edit' | translateWF: 'Add or edit manual entries on this account'"
                            (click)="openLineModal(line)"
                          />
                          <p-button icon="pi pi-trash" severity="danger" [text]="true" [rounded]="true" (click)="removeExistingLine(line.lineId)" />
                        </td>
                      }
                    </tr>
                  </ng-template>

                  <!-- The breakdown: everything planned against this account for the week. -->
                  <ng-template pTemplate="rowexpansion" let-line>
                    <tr>
                      <td [attr.colspan]="isDraft() ? 8 : 7" class="p-0">
                        <div class="p-4 bg-[var(--p-content-hover-background)]">
                          <p-table [value]="line.breakdown" styleClass="p-datatable-sm">
                            <ng-template pTemplate="header">
                              <tr>
                                <th style="width: 9rem">{{ 'CashRequest_EntryType' | translateWF: 'Source' }}</th>
                                <th>{{ 'CashRequest_Entry' | translateWF: 'Entry' }}</th>
                                <th class="text-right" style="width: 14rem">
                                  {{
                                    'CashRequest_SourceAmount'
                                      | translateWF
                                        : 'Source
                    amount'
                                  }}
                                </th>
                                <th class="text-right" style="width: 10rem">{{ 'CashRequest_Usd' | translateWF: 'USD' }}</th>
                                @if (isDraft()) {
                                  <th style="width: 4rem"></th>
                                }
                              </tr>
                            </ng-template>

                            <ng-template pTemplate="body" let-entry>
                              <tr>
                                <td>
                                  @if (isDocumentBacked(entry)) {
                                    <p-tag severity="info" [value]="'CashRequest_Entry_Document' | translateWF: 'Document'" />
                                  } @else {
                                    <p-tag severity="secondary" [value]="'CashRequest_Entry_Manual' | translateWF: 'Manual'" />
                                  }
                                </td>
                                <td>
                                  <div class="col">
                                    @if (isDocumentBacked(entry)) {
                                      <!-- Opens in a new tab: the request is mid-assembly and must not be navigated away from. -->
                                      <a
                                        class="font-mono cursor-pointer text-primary hover:underline"
                                        [title]="'CashRequest_OpenSource' | translateWF: 'Open this document in a new tab'"
                                        (click)="openSourceDocument(entry)"
                                        >{{ entryLabel(entry) }}<i class="pi pi-external-link text-xs ml-1"></i
                                      ></a>
                                    } @else {
                                      <span class="font-mono">{{ entryLabel(entry) }}</span>
                                    }
                                    @if (entry.sourceDocument?.number && entry.description) {
                                      <span class="text-xs text-[var(--p-text-muted-color)]">{{ entry.description }}</span>
                                    }
                                  </div>
                                </td>
                                <td class="text-right tabular-nums">
                                  {{ entry.sourceAmount | number: '1.2-2' }}
                                  <span class="text-xs text-[var(--p-text-muted-color)]">{{ currencyCode(entry.sourceCurrency) }}</span>
                                </td>
                                <td class="text-right tabular-nums">{{ entry.functionalAmount | number: '1.2-2' }}</td>
                                @if (isDraft()) {
                                  <td class="text-center">
                                    @if (isDocumentBacked(entry)) {
                                      <p-button
                                        icon="pi pi-times"
                                        severity="danger"
                                        [text]="true"
                                        [rounded]="true"
                                        [pTooltip]="'CashRequest_Unplan' | translateWF: 'Remove document'"
                                        (click)="unplanEntry(entry)"
                                      />
                                    }
                                  </td>
                                }
                              </tr>
                            </ng-template>

                            <ng-template pTemplate="emptymessage">
                              <tr>
                                <td [attr.colspan]="isDraft() ? 5 : 4" class="text-center text-[var(--p-text-muted-color)]">
                                  {{ 'CashRequest_NoEntries' | translateWF: 'Nothing planned against this account yet.' }}
                                </td>
                              </tr>
                            </ng-template>
                          </p-table>

                          <!-- Reachable from inside the breakdown too: this is where the entries are, so it is
                             where someone looking to add one will reach for it. -->
                          @if (isDraft()) {
                            <div class="row-end-stretch mt-2">
                              <p-button
                                [label]="'CashRequest_Line_AddEntry' | translateWF: 'Add entry'"
                                icon="pi pi-plus"
                                size="small"
                                [text]="true"
                                (click)="openLineModal(line)"
                              />
                            </div>
                          }
                        </div>
                      </td>
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="emptymessage">
                    <tr>
                      <td [attr.colspan]="isDraft() ? 8 : 7" class="text-center py-6 text-[var(--p-text-muted-color)]">
                        @if (isDraft()) {
                          {{
                            'CashRequest_Empty_Draft'
                              | translateWF
                                : 'Nothing planned yet — use "Plan documents" for payment requests and withholdings, or "Add line" for
            payroll and anything else with no document.'
                          }}
                        } @else {
                          {{ 'CashRequest_Empty' | translateWF: 'This request has no lines.' }}
                        }
                      </td>
                    </tr>
                  </ng-template>

                  <ng-template pTemplate="footer">
                    @if (hasLines()) {
                      <tr>
                        <td colspan="5" class="text-right font-semibold">{{ 'CashRequest_Total' | translateWF: 'Total (USD)' }}</td>
                        <td class="text-right font-semibold tabular-nums">{{ req.totalAmount | number: '1.2-2' }}</td>
                        <td></td>
                        @if (isDraft()) {
                          <td></td>
                        }
                      </tr>
                    }
                  </ng-template>
                </p-table>
              </p-card>
            </p-panel>

            <!-- ── What has actually been spent against the plan (#4796) ──────────────
                 Read-side only: the link is the per-line CashRequest tag on each AP payment allocation, so
                 nothing here is stored on the request and nothing can drift out of step with the payments. -->
            @if (usage(); as use) {
              <p-panel [header]="'CashRequest_Usage' | translateWF: 'How this request has been used'">
                <ng-template pTemplate="icons">
                  <p-tag [severity]="usageStateSeverity()" [value]="usageStateLabel()" />
                </ng-template>
                <div class="col-sm">
                  <p-card>
                    <div class="col-sm">
                      <div class="flex items-baseline justify-between">
                        <span>{{ 'CashRequest_Usage_Planned' | translateWF: 'Planned' }}</span>
                        <span class="tabular-nums font-semibold">{{ use.plannedAmount | number: '1.2-2' }} USD</span>
                      </div>
                      <div class="flex items-baseline justify-between">
                        <span>{{ 'CashRequest_Usage_Applied' | translateWF: 'Applied to AP payments' }}</span>
                        <span class="tabular-nums font-semibold">{{ use.appliedAmount | number: '1.2-2' }} USD</span>
                      </div>

                      <p-progressbar [value]="usageBarPercent()" [showValue]="false" styleClass="h-2" />

                      <div class="flex items-baseline justify-between flex-wrap gap-2">
                        <span class="text-sm text-[var(--p-text-muted-color)]">
                          {{ use.appliedPercent | number: '1.0-0' }}% {{ 'CashRequest_Usage_AppliedSuffix' | translateWF: 'applied' }} · {{ use.paymentCount }}
                          {{ 'CashRequest_Usage_Payments' | translateWF: 'documents' }}
                          <!-- Draft money is committed on paper but has not left the bank — worth naming, not blending. -->
                          @if (use.draftAmount > 0) {
                            · {{ use.draftAmount | number: '1.2-2' }} USD
                            {{
                              'CashRequest_Usage_InDraft'
                                | translateWF
                                  : 'still in
                  draft'
                            }}
                          }
                        </span>
                        @if (isOverdrawn()) {
                          <span class="font-semibold tabular-nums text-red-500">
                            {{ overdrawnAmount() | number: '1.2-2' }} USD {{ 'CashRequest_Usage_OverBy' | translateWF: 'over plan' }}
                          </span>
                        } @else {
                          <span class="font-semibold tabular-nums">
                            {{ use.remainingAmount | number: '1.2-2' }} USD {{ 'CashRequest_Usage_Unused_Suffix' | translateWF: 'unused' }}
                          </span>
                        }
                      </div>
                    </div>
                  </p-card>

                  <p-card data-variant="compact">
                    <p-table [value]="usageLines()" styleClass="p-datatable-sm">
                      <ng-template pTemplate="header">
                        <tr>
                          <th style="width: 20rem">{{ 'CashRequest_Usage_Payment' | translateWF: 'Paid by' }}</th>
                          <th>{{ 'CashRequest_Usage_Invoice' | translateWF: 'Invoice' }}</th>
                          <th style="width: 9rem">{{ 'CashRequest_Usage_Status' | translateWF: 'Status' }}</th>
                          <th class="text-right" style="width: 13rem">{{ 'CashRequest_Usage_AppliedCol' | translateWF: 'Applied' }}</th>
                        </tr>
                      </ng-template>

                      <ng-template pTemplate="body" let-line>
                        <tr>
                          <td>
                            <div class="col leading-tight">
                              <div class="row-sm-start-center">
                                <!-- New tab: the request is mid-assembly and must not be navigated away from. -->
                                <a
                                  class="font-medium cursor-pointer text-primary hover:underline"
                                  [title]="'CashRequest_Usage_OpenPayment' | translateWF: 'Open this document in a new tab'"
                                  (click)="openPayment(line)"
                                  >{{ line.sourceNumber }}<i class="pi pi-external-link text-xs ml-1"></i
                                ></a>
                                <!-- A remittance pays SAR, not a provider — worth saying, since every other row does. -->
                                @if (isRemittance(line)) {
                                  <p-tag severity="warn" [value]="'CashRequest_Usage_Sar' | translateWF: 'SAR'" />
                                }
                              </div>
                              <span class="text-xs text-[var(--p-text-muted-color)]">
                                {{ line.date | date: 'dd/MM/yyyy' }}
                                @if (bankLabel(line)) {
                                  · {{ bankLabel(line) }}
                                }
                              </span>
                            </div>
                          </td>
                          <td>
                            <div class="row-sm-start-center">
                              <app-document-identity
                                [documentNumber]="line.documentNumber"
                                [fiscalNumber]="line.fiscalNumber"
                                [clickable]="!!line.documentReference"
                                [linkTitle]="'CashRequest_Usage_OpenInvoice' | translateWF: 'View this invoice'"
                                (documentClick)="openDocumentQuickView(line.documentReference)"
                              />
                              @if (line.documentReference) {
                                <p-button
                                  icon="pi pi-eye"
                                  [text]="true"
                                  [rounded]="true"
                                  [pTooltip]="'CashRequest_Usage_OpenInvoice' | translateWF: 'View this invoice'"
                                  (click)="openDocumentQuickView(line.documentReference)"
                                />
                              }
                            </div>
                            <div class="col leading-tight">
                              @if (line.retentionNumber) {
                                <!-- On a remittance the Constancia is what was paid; the invoice above only identifies it. -->
                                <span class="text-xs text-[var(--p-text-muted-color)]">{{ 'CashRequest_Usage_Via' | translateWF: 'Retention' }} {{ line.retentionNumber }}</span>
                              }
                              @if (line.providerName) {
                                <span class="text-xs text-[var(--p-text-muted-color)]">{{ line.providerName }}</span>
                              }
                            </div>
                          </td>
                          <td>
                            <p-tag [severity]="paymentStatusSeverity(line.status)" [value]="paymentStatusLabel(line.status) | translateWF: paymentStatusLabel(line.status)" />
                          </td>
                          <td class="text-right">
                            <div class="col leading-tight">
                              <span class="font-semibold tabular-nums">{{ line.appliedAmount | number: '1.2-2' }} {{ currencyCode(line.currency) }}</span>
                              <!-- Only when it says something the line above does not. -->
                              @if (showsFunctional(line)) {
                                <span class="text-xs text-[var(--p-text-muted-color)] tabular-nums">≈ {{ line.appliedFunctionalAmount | number: '1.2-2' }} USD</span>
                              }
                            </div>
                          </td>
                        </tr>
                      </ng-template>

                      <ng-template pTemplate="emptymessage">
                        <tr>
                          <td colspan="4" class="text-center py-6 text-[var(--p-text-muted-color)]">
                            {{ 'CashRequest_Usage_Empty' | translateWF: 'Nothing has drawn money from this request yet.' }}
                          </td>
                        </tr>
                      </ng-template>
                    </p-table>
                  </p-card>
                </div>
                <ng-template pTemplate="footer"
                  ><small class="text-surface-500 font-normal">{{
                    'CashRequest_Usage_Hint'
                      | translateWF
                        : 'AP payment and withholding remittance lines that drew money from
            this cash request.'
                  }}</small>
                </ng-template>
              </p-panel>
            }
          }
        }
      </div>

      <!-- Right column: request total / breakdown -->
      <div class="span-12-12-3">
        <p-panel class="sticky-summary" [header]="'CashRequest_RequestTotal' | translateWF: 'Request total'">
          <p-card>
            <p class="hint">
              {{
                'CashRequest_PlanningHint'
                  | translateWF
                    : 'A cash request is a planning container — it does
            not post to the general ledger.'
              }}
            </p>
            <app-cash-request-breakdown [lines]="lines()" currencyLabel="USD" />
          </p-card>
        </p-panel>
      </div>
    </div>
  </div>
</main>
